import React, { useState } from 'react';

import styled from 'styled-components';

import { Flex } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import Theme, { StylableComponent } from '@adsk/alloy-react-theme';

import TrialModal from './components/TrialModal';
import {
  AUTOSPECS_TRIAL_MARKETING_URL,
  OFFERING_NAMES,
  PRICING_URL,
} from './components/TrialModalItem/helpers/const';
import { buttonStyle, messageStyle } from './helpers/styles';
import {
  BannerMessage,
  Subscription,
  SubscriptionConstruction,
} from './helpers/types';
import { useTranslation } from './i18n';

type TrialBannerViewProps = StylableComponent<HTMLDivElement> & {
  onClickAnalytics: () => void;
  displayButton: boolean | null;
  message: BannerMessage | null;
  userTrials: Subscription[] | SubscriptionConstruction[];
};

export const MessageDiv = styled.div(messageStyle);

const TrialBannerView = React.forwardRef<HTMLDivElement, TrialBannerViewProps>(
  (
    {
      displayButton = false,
      message = null,
      userTrials,
      onClickAnalytics,
      className,
      style,
      color: _color, // this prop is ignored to fix an error with Box getting wrong color type
      ...props
    },
    ref,
  ) => {
    const [show, setShow] = useState(false);
    const { t } = useTranslation();

    const handleClickBuyingOptions = () => {
      const justOneTrial = userTrials && userTrials.length === 1;

      if (!justOneTrial) {
        setShow(true);
      } else {
        const offeringCategory =
          'offeringCategory' in userTrials[0]
            ? userTrials[0].offeringCategory
            : ''; // empty string means the pricing home page
        const nameContainsAutoSpecs = userTrials[0].offeringName.includes(
          OFFERING_NAMES.AUTOSPECS,
        );
        const buyingOptionsLink = nameContainsAutoSpecs
          ? AUTOSPECS_TRIAL_MARKETING_URL
          : `${PRICING_URL}/${offeringCategory}`;
        window.open(buyingOptionsLink, '_blank');
      }
      onClickAnalytics?.();
    };

    return (
      <Flex
        className={className}
        flexDirection="row"
        style={style}
        ref={ref}
        data-testid="TrialBanner"
        {...props}
      >
        <MessageDiv
          style={{
            color:
              message && message.daysLeft <= 3
                ? Theme.colors.red500
                : Theme.colors.charcoal900,
          }}
        >
          {message ? message.text : ''}
        </MessageDiv>
        {displayButton && (
          <Button
            data-testid="TrialBanner_button"
            style={buttonStyle}
            variant={Button.VARIANTS.PRIMARY}
            onClick={handleClickBuyingOptions}
          >
            {t('BANNER_BUTTON_LABEL')}
          </Button>
        )}
        <TrialModal
          data-testid="TrialBanner_modal"
          onClose={() => {
            setShow(false);
          }}
          show={show}
          userTrials={userTrials}
        />
      </Flex>
    );
  },
);

TrialBannerView.displayName = 'TrialBannerView';

export default TrialBannerView;
